import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiShield, FiAward, FiZap, FiTarget, FiBook, FiHeart, FiStar } from 'react-icons/fi';

const GuaranteeSection = () => {
  const [showRandomFacts, setShowRandomFacts] = useState(false);

  const credentials = [
    {
      icon: FiAward,
      title: "Academic Excellence",
      description: "Trained at Purdue University, a top program for speech-language pathology and cognitive rehabilitation"
    },
    {
      icon: FiStar,
      title: "Elite Recognition",
      description: "David Knox Fellowship recipient (awarded to less than 0.02% of individuals)"
    },
    {
      icon: FiZap,
      title: "Scientific Background",
      description: "Former NASA scientist"
    },
    {
      icon: FiTarget,
      title: "Presidential Honor",
      description: "Outstanding Student Award recipient from President Obama"
    },
    {
      icon: FiBook,
      title: "Academic Success Focus",
      description: "Advanced training in cognitive-communication therapy and learning rehabilitation"
    },
    {
      icon: FiHeart,
      title: "Empathetic Guide",
      description: "Combines scientific expertise with deep understanding of how learning differences affect families"
    }
  ];

  const randomFacts = [
    "Mildly obsessed with French fries",
    "Whodunit lover"
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="card-featured max-w-4xl mx-auto">
            <div className="w-20 h-20 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <SafeIcon icon={FiShield} className="h-10 w-10 text-accent-gold" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6">
              Real Academic Progress in 90 Days, Guaranteed.
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              We believe every brilliant brain can learn with the right executive function foundation. If you don't see measurable improvement in your child's academic performance and learning confidence within 90 days, we'll work with you until you do—or offer a refund. Because their intelligence deserves to shine, and their academic future is too important to leave to chance.
            </p>
            <button onClick={scrollToContact} className="btn-gold text-lg px-10 py-5">
              Start Your Risk-Free Journey
            </button>
          </div>
        </motion.div>

        {/* Meet Sanna Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6">
              Meet <span id="sanna-name" className="relative">Sanna</span> Darwish, Your Expert in Cognitive Learning Therapy
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170088935-blob"
                alt="Sanna Darwish, M.S. CCC-SLP"
                className="rounded-2xl shadow-lg w-full"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-accent-magenta">M.S. CCC-SLP</div>
                  <div className="text-sm text-gray-600">EF Specialist</div>
                </div>
              </div>
            </motion.div>

            {/* Bio */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <p className="text-lg text-gray-700 leading-relaxed">
                💡 <strong>Parent Partnership:</strong> Believes parents are the most important part of their child's learning team
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This isn't generic academic support. It's specialized cognitive therapy designed to unlock the brilliant potential in every differently-wired brain.
              </p>
              <p className="text-lg font-semibold text-gray-900">
                Let's build your child's learning foundation for lifelong academic success—starting today.
              </p>
              <button
                onClick={() => setShowRandomFacts(!showRandomFacts)}
                className="btn-secondary"
              >
                {showRandomFacts ? 'Hide' : 'Unlock'} random facts
              </button>
            </motion.div>
          </div>

          {/* Random Facts */}
          {showRandomFacts && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="relative mb-12"
            >
              {randomFacts.map((fact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className={`absolute ${
                    index === 0 ? 'top-0 left-1/4' : 'top-8 right-1/4'
                  } bg-accent-yellow p-3 rounded-lg shadow-lg max-w-xs`}
                >
                  <div className="text-sm font-medium text-gray-900">{fact}</div>
                  <div className={`absolute ${
                    index === 0 ? 'bottom-full left-8' : 'bottom-full right-8'
                  } border-l-8 border-r-8 border-b-8 border-transparent border-b-accent-yellow`}></div>
                </motion.div>
              ))}
              <div className="h-24"></div>
            </motion.div>
          )}

          {/* Credentials Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((credential, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card text-center"
              >
                <div className="w-12 h-12 bg-accent-magenta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={credential.icon} className="h-6 w-6 text-accent-magenta" />
                </div>
                <h3 className="font-bold text-gray-900 mb-3">{credential.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{credential.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GuaranteeSection;